import SignupFormProvider from '../fakeCore/SignupFormContainer';
import useAuth from '@jetshop/core/components/AuthContext/useAuth';
import t, { Intl } from '@jetshop/intl';
import React, { useContext, useState } from 'react';
import { styled } from 'linaria/react';
import { cx } from 'linaria';
import { Redirect } from 'react-router';
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig';
import Head from '@jetshop/core/components/Head';
import { useLocation } from 'react-router';
import BaseMaxWidth from '../../Layout/MaxWidth';
import LoadingPage from '../../LoadingPage';
import { smallCaps } from '../../ui/Headings';
import Address from '../Signup/Address';
import LoginFields from '../Signup/LoginFields';
import { theme } from '../../Theme';
import { SignupFormikContext } from '@jetshop/core/components/Auth/signup-context';
import emailMisspelled, { top100 } from 'email-misspelled';
import heroImage from '../../../img/NewLoginImg.jpg';
import ClubHooksUSP from './ClubHooksUSP';
import { Above } from '@jetshop/ui/Breakpoints';
import { ReactComponent as ClubHooksLogo } from '../../MyPages/imgs/clubhooks_shield.svg';
import { normalizeLookupCustomer } from '../Signup/isUnchangedMaskedValue';

const RegisterWrapper = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr auto;

  ${theme.below.lg} {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(100px, 300px) 1fr auto;
    height: auto;
  }

  .hero-section {
    position: relative;
    grid-area: 1 / 1 / 3 / 2;
    display: flex;
    align-items: center;
    justify-content: center;

    ${theme.below.lg} {
      grid-area: 1 / 1 / 2 / 2;
    }

    .image-container {
      width: 100%;
      height: 100%;
      background-image: url(${heroImage});
      background-size: cover;      
      background-position: center center; /* Shows center-right portion */
      background-repeat: no-repeat;

      /* filter: contrast(1.2) saturate(1.25); */
    }

    .content {
      position: absolute;
      top: 25%;
      left: 50%;
      width: 300px;
      transform: translate(-50%, -50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      .logo-container {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      ${theme.below.lg} {
        top: 50%;
        width: 200px;
      }
    }
  }

  .form-section {
    grid-area: 1 / 2 / 2 / 3;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: white;
    margin: 0 auto;
    padding: 2rem 0;
    width: 100%;
    ${theme.below.lg} {
      grid-area: 2 / 1 / 3 / 2;
    }

    .content {
      padding: 0 2rem;
      width: 100%;
      max-width: 55rem;
      ${theme.below.lg} {
        padding: 0 1rem;
        max-width: 25rem;
      }
    }

    header {
      align-self: flex-start;
      margin-bottom: 2rem;
      h1 {
        margin: 0 0 1rem 0;
      }
      p {
        margin: 0;
      }
    }

    section {
      padding: 0 !important;
    }
  }

  .club-hooks-section {
    grid-area: 2 / 2 / 3 / 3;
    width: 100%;
    height: 100%;
    ${theme.below.lg} {
      grid-area: 3 / 1 / 4 / 2;
    }
  }

  .pid-mobile-fields {
    width: 100%;
    margin-bottom: -1.2rem;
  }

  .email-password-fields .section > .input-wrapper,
  .pid-mobile-fields .input-wrapper {
    min-width: 200px;
    max-width: 350px;
  }

  .email-password-fields,
  .pid-mobile-fields {
    input {
      border: none;
      height: 3rem;
      background-color: ${theme.colors.white};
      border-bottom: 1px solid ${theme.colors.darkgrey};
      padding: 0.4rem;
      &::placeholder {
        color: ${theme.colors.grey};
        font-size: 14px;
        font-weight: 300;
        opacity: 0.7;
      }

      // For older browsers
      &::-webkit-input-placeholder { /* Chrome/Safari/Opera */
        color: ${theme.colors.grey};
        font-size: 14px;
        opacity: 0.7;
      }
      &::-moz-placeholder { /* Firefox */
        color: ${theme.colors.grey};
        font-size: 14px;
        opacity: 0.7;
      }
      &:-ms-input-placeholder { /* IE/Edge */
        color: ${theme.colors.grey};
        font-size: 14px;
        opacity: 0.7;
      }
    }
  }
`;

const MaxWidth = styled(BaseMaxWidth)`
  align-items: center;
  p {
    line-height: 1.5;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }
  button {
    width: 100%;
    max-width: 100%;
  }
  .hint {
    margin-top: 0.5rem;
    font-style: italic;
    font-size: 0.75rem;
    text-align: center;
  }
`;

function HeaderWithStatus({ status }) {
  if (!status) {
    return (
      <>
        <p>
          {t(
            'Enter your details to become a member.'
          )}
        </p>
      </>
    );
  }

  if (status.isAdditionalDataRequired) {
    return (
      <>
        <p>{t("We're missing some key information.")}</p>
        <p>{t('Please fill in required fields to continue.')}</p>
      </>
    );
  }

  if (status.isNonExistingCustomer) {
    return (
      <>
        <p>
          {t('No customer found with entered { lookupKey }.', {
            lookupKey: 'e-mail'
          })}
        </p>
        <p>
          {t(
            'Please fill in your contact details for shipping and communication.'
          )}
        </p>
      </>
    );
  }
  return null;
}

const ErrorWrapper = styled.div`
  background: #ffe58d;
  padding: 2rem 1rem 1rem;
  border-radius: 0.25rem;
  border: 1px solid #ffce00;
  position: relative;
  margin: 1rem 0 0;
  span {
    position: absolute;
    font-weight: bold;
    top: 0.5rem;
    left: 1rem;
    font-size: 0.75rem;
  }
  .signup-login-button {
    background: ${theme.colors.accent};
    border: 1px solid ${theme.colors.accent};
    color: ${theme.colors.white};
    text-decoration: none;
    text-align: center;
    padding: 0.5rem 1rem;
    border-radius: 0.25rem;
    width: 100%;
    cursor: pointer;
    margin-top: 1rem; 
  }
`;

export const Errors = () => {
  const {
    formik: { status }
  } = useContext(SignupFormikContext);
  const errorRef = React.useRef(null);

  let fieldLabel = '';
  let message = 'Invalid value';

  switch (status?.validationDetails?.[0]) {
    case 'EmailAddress':
      fieldLabel = 'E-mail';  
      if (status?.validationDetails?.[1] === 'UniqueValueAlreadyExists') {
        message = 'You have already signed up with this email address';
      }
      break;

    case 'MobilePhone':
      fieldLabel = 'Telefonnummer';
      break;

    case 'Pid':
      fieldLabel = 'National ID/Social Security number';
      if (status?.validationDetails?.[1] === 'UniqueValueAlreadyExists') {
        message =
          'This social security number is already used in another account. If you can not access your registered account, please contact customer service.';
      }
      break;
    default:
      fieldLabel = 'ERROR';
  }

  React.useEffect(() => {
    if (status?.isSignupError) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [status?.isSignupError, status?.validationDetails]);

  if (status?.isSignupError) {
    return (
      <ErrorWrapper ref={errorRef} role="alert">
        <span>{t(fieldLabel)}</span>
        <p>{t(message)}</p>
      </ErrorWrapper>
    );
  }

  return null;
};

function VoyadoSignup() {
  const { loggedIn } = useAuth();
  const { routes } = useShopConfig();
  const { state } = useLocation();
  const [emailCheck, setEmailCheck] = useState([]);
  const lookupData = normalizeLookupCustomer(state?.customer);
  if (loggedIn) {
    return <Redirect to={routes.myPages.path} />;
  }
  const emailChecker = emailMisspelled({ maxMisspelled: 3, domains: top100 });

  return (
    <RegisterWrapper>
      <section className='hero-section' aria-label='Club Hööks Hero'>
        <div className="image-container">
          
        </div>
        <div className="content">
          <div className="logo-container">
            <ClubHooksLogo />
          </div>
        </div>
      </section>

      <section className="form-section">
        <div className="content">
          <SignupFormProvider
            LoadingComponent={<LoadingPage />}
            lookupData={lookupData}
            >
            <div className="form-container" style={{ marginTop: '2em' }}>
              <Intl>{t => <Head data={{ title: t('Signup') }} />}</Intl>
              <header>
                <h1
                  className={cx('heading', smallCaps)}
                  data-testid="signup-page-header"
                >
                  {t('Become a member')}
                </h1>
                <HeaderWithStatus status={state?.status} />
                <Errors />
              </header>
              <div className="pid-mobile-fields">
                <Address />
              </div>
              <div className="email-password-fields">
                <LoginFields
                  emailCheck={emailCheck}
                  setEmailCheck={setEmailCheck}
                  emailChecker={emailChecker}
                />
              </div>
            </div>
          </SignupFormProvider> 
        </div>
      </section>
      <Above breakpoint="lg">
        {matches =>
          matches && (
            <section className="club-hooks-section">
              <ClubHooksUSP  />
            </section>
          )
        }
      </Above>
    </RegisterWrapper>
  );
}
export default VoyadoSignup;
